<template>
    <div>
        <b-container fluid>
            <div class="my-3">
                <div class="h3 m-0 mr-3">
                    Standards
                </div>
            </div>

            <div class="d-flex align-items-center mb-3">
                <div class="flex-grow-1 mr-3">
                    <b-form @submit.prevent="searchStandards">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <div class="row align-items-center">
                                    <div class="col-lg mb-lg-0 mb-1"
                                         :class="{'is-invalid': searched && !active_filters}">
                                        <multiselect
                                            v-model="filters.state"
                                            :options="states"
                                            label="name"
                                            track-by="name"
                                            placeholder="State"
                                        />
                                    </div>
                                    <div class="col-lg pl-lg-0 mb-lg-0 mb-1"
                                         :class="{'is-invalid': searched && !active_filters}">
                                        <multiselect
                                            v-model="filters.subject"
                                            :options="subjects"
                                            label="name"
                                            track-by="name"
                                            placeholder="Subjects"
                                        />
                                    </div>
                                    <div class="col-lg pl-lg-0 mb-lg-0 mb-1"
                                         :class="{'is-invalid': searched && !active_filters}">
                                        <multiselect
                                            v-model="filters.low_grade"
                                            :options="grades"
                                            label="grade"
                                            track-by="grade"
                                            :custom-label="formatGradeLabel"
                                            placeholder="Low Grade"
                                        />
                                    </div>
                                    <div class="col-lg pl-lg-0 mb-lg-0 mb-1"
                                         :class="{'is-invalid': searched && !active_filters}">
                                        <multiselect
                                            v-model="filters.high_grade"
                                            :options="grades"
                                            label="grade"
                                            track-by="grade"
                                            :custom-label="formatGradeLabel"
                                            placeholder="High Grade"
                                        />
                                    </div>
                                    <div class="col-lg pl-lg-0 mb-lg-0"
                                         :class="{'is-invalid': searched && !active_filters}">
                                        <multiselect
                                            v-model="filters.year"
                                            :options="years"
                                            label="year"
                                            track-by="year"
                                            placeholder="Year"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div v-if="active_filters" class="ml-3">
                                <b-btn variant="danger" @click="clearFilters">
                                    <font-awesome-icon :icon="['fad', 'backspace']" />
                                    Clear All Filters
                                </b-btn>
                            </div>
                            <div class="ml-3">
                                <b-btn type="submit" variant="outline-secondary">
                                    <font-awesome-icon :icon="['fad', 'search']" />
                                </b-btn>
                            </div>
                        </div>
                    </b-form>
                </div>
                <div>
                    <b-button variant="success" @click.prevent="newStandard">
                        <font-awesome-icon :icon="['fad', 'plus-square']" />
                        Create Standard
                    </b-button>
                </div><div>
                    <b-button class="ml-3" variant="outline-success" @click.prevent="newCSVImport">
                        <font-awesome-icon :icon="['fad', 'plus-square']" />
                        Import Standards CSV
                    </b-button>
                </div>
            </div>

            <div v-if="loading_standards || searching">
                <b-spinner label="Loading..."></b-spinner>
            </div>

            <div v-else>
                <div v-if="searched && standards && standards.length">
                    <b-table
                        :items="standards"
                        :fields="fields"
                        striped
                        responsive
                        sticky-header
                        outlined
                        no-border-collapse
                        head-variant="light"
                    >
                        <template v-slot:head(number)>
                            Code
                        </template>

                        <template v-slot:head(guid)>
                            GUID
                        </template>

                        <template v-slot:head(low_grade)>
                            Low Grade
                        </template>

                        <template v-slot:head(high_grade)>
                            High Grade
                        </template>

                        <template #cell(subject)="data">
                            <span class="text-capitalize">
                                {{ formattedSubject(data.item.subject) }}
                            </span>
                        </template>
                        <template #cell(low_grade)="data">
                            <template v-if="data.item.low_grade == 0">
                                K
                            </template>
                            <template v-else>
                                {{ data.item.low_grade }}
                            </template>
                        </template>
                        <template #cell(high_grade)="data">
                            <template v-if="data.item.high_grade == 0">
                                K
                            </template>
                            <template v-else>
                                {{ data.item.high_grade }}
                            </template>
                        </template>
                        <template #cell(actions)="data">
                            <div class="d-flex align-items-center flex-nowrap">
                                <div class="mx-auto">
                                    <b-dropdown variant="secondary" dropleft right>
                                        <template #button-content>
                                            <font-awesome-icon :icon="['fad', 'cog']" />
                                        </template>
                                        <b-dropdown-item @click.prevent="editStandard(data.item)">
                                            <font-awesome-icon :icon="['fad', 'edit']" class="text-primary"
                                                               fixed-width />
                                            Edit
                                        </b-dropdown-item>
                                    </b-dropdown>
                                </div>
                            </div>
                        </template>
                    </b-table>
                </div>
                <div v-else-if="searched && !active_filters">
                    <b-alert variant="danger" show>
                        Please select at least one filter
                    </b-alert>
                </div>
                <div v-else-if="searched && active_filters && (!standards || standards && !standards.length)">
                    <b-alert variant="danger" show>
                        0 results
                    </b-alert>
                </div>
            </div>
        </b-container>

        <StandardsImportCSVModal
            v-model="show_csv_modal"
            :years="years"
            @updated="standardUpdated"
            @hide="show_csv_modal = false"
        />
        <standard-modal
            v-model="show_standard_modal"
            :standard="standard"
            :standards="standards"
            :years="years"
            @created="standardCreated"
            @updated="standardUpdated"
            @hide="show_standard_modal = false"
        />
    </div>
</template>

<script>
import states from '@/common/states'
import grades from '@/common/grades'
import standard_subjects from '@/common/standard_subjects'
import StandardModal from '@/components/modals/StandardModal'
import StandardsImportCSVModal from '@/components/modals/StandardsImportCSVModal'

import moment from 'moment'

export default {
    data: () => ({
        standards: [],

        fields: [
            {
                key: 'number',
                class: 'text-uppercase',
                sortable: true
            },
            {
                key: 'guid',
                sortable: true
            },
            {
                key: 'state',
                sortable: true
            },
            {
                key: 'subject',
                sortable: true
            },
            {
                key: 'description',
                sortable: true
            },
            {
                key: 'year',
                sortable: true
            },
            {
                key: 'low_grade',
                sortable: true
            },
            {
                key: 'high_grade',
                sortable: true
            },
            {
                key: 'actions',
                sortable: false
            },
        ],

        years: [],
        grades: grades,
        subjects: standard_subjects,

        standard: {},

        filters: {},

        searched: false,
        searching: false,

        loading_standards: false,

        show_standard_modal: false,

        show_csv_modal: false,

        filters_visible: false
    }),
    props: [],
    components: {
        StandardModal,
        StandardsImportCSVModal,
    },
    computed: {
        states() {
            const national = {
                name: 'National',
                abbreviation: 'NA',
            }

            return [national, ...states]
        },
        active_filters() {
            let active_filters = false

            if (
                this.filters &&
                _.some(this.filters)
            ) {
                active_filters = true
            }

            return active_filters
        }
    },
    methods: {
        newStandard() {
            this.standard = {}

            this.show_standard_modal = true
        },
        newCSVImport() {
            this.show_csv_modal = true
        },
        editStandard(standard) {
            this.standard = _.cloneDeep(standard)

            this.show_standard_modal = true
        },
        async searchStandards() {
            let terms = {}

            this.standards = []

            if (!this.active_filters) {
                this.searched = true
                return false
            }

            if (this.filters.state) {
                terms.state = this.filters.state.abbreviation
            }
            if (this.filters.subject) {
                terms.subject = this.filters.subject.name
            }
            if (this.filters.year) {
                terms.year = this.filters.year.year
            }
            if (this.filters.low_grade) {
                terms.low_grade = this.filters.low_grade.grade
            }
            if (this.filters.high_grade) {
                terms.high_grade = this.filters.high_grade.grade
            }

            this.loading_standards = true
            this.searching = true
            this.searched = false

            try {
                const response = await this.axios.post('/api/standards/search', terms)
                if (response.data) {
                    this.standards = response.data
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to search standards: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.loading_standards = false
                this.searching = false
                this.searched = true
            }
        },
        standardCreated() {
            this.show_standard_modal = false
        },
        standardUpdated() {
            this.searchStandards()

            this.show_standard_modal = false
        },
        formattedSubject(subject) {
            if (subject) {
                return subject.replace(/_/g, " ")
            }
        },
        formatGradeLabel(option) {
            if (option.grade == 0 || option.grade == -1) {
                return option.name
            }

            return option.grade
        },
        clearFilters() {
            this.filters = {}

            this.searched = false
        },
        setYearsOptions() {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const last25Years = [];

            // Loop through the past 25 years and add to array
            for (let i = 0; i < 25; i++) {
                const year = currentYear - i;
                last25Years.push({year: year.toString()});
            }
            this.years = last25Years;
        }
    },
    mounted() {
        this.setYearsOptions();
    }
}
</script>
